import { ReactElement } from 'react';

import { ParametersExceptFirst } from 'ia-react-core/~/interfaces/ParametersExceptFirst';
import { ResourceState } from 'ia-react-core/~/interfaces/ResourceState';
import withSchema from 'ia-react-core/~/hocs/schema/withSchema';

import compose from '../../../utils/components/_compose';
import withUniqueId from '../../../hocs/helpers/_withUniqueId';
import getDataDst from '../../../utils/components/_getDataDst';
import resolveScope from '../../../utils/redux/_resolveScope';
import injectReducer from '../../../hocs/redux/injectReducer';
import withResourceReducerKey from '../../../hocs/resource/_withResourceReducerKey';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import cancelResourceById from '../../../actions/resource-fn/cancelResourceById';
import ResourceGroup, { ResourceGroupComposedProps, ResourceGroupDataItem, ResourceGroupProps } from './ResourceGroup';
import resourceGroupReducer from './ResourceGroup.reducer';
import resourceGroupDataReducer from './ResourceGroup.dataReducer';

const ComposedResourceGroup: (<
  DataList extends ResourceGroupDataItem[],
  Error,
  StateData
>(
  props: ResourceGroupProps<DataList, Error, StateData>
) => ReactElement<ResourceGroupProps<DataList, Error, StateData>>) & {
  type: typeof ResourceGroup.type;
  defaultProps: typeof ResourceGroup.defaultProps;
} = compose<any>(
  withUniqueId('resourceId'),
  withSchema('resource'),
  withResourceReducerKey,
  injectReducer<ResourceGroupComposedProps<any, any, any>, Partial<ResourceState>>({
    key: ({ resourceReducerKey }) => resourceReducerKey,
    idName: 'resourceId',
    namespace: 'Core/Resource',
    identifiers: ({ resourceId, name }) => ({ resourceId, name }),
    reducer: resourceGroupReducer,
    actions: ({ resourceId }) => ({
      requestResource: (...args: ParametersExceptFirst<typeof requestResourceById>) => requestResourceById(resourceId, ...args),
      cancelResource: (...args: ParametersExceptFirst<typeof cancelResourceById>) => cancelResourceById(resourceId, ...args),
    }),
    initialState: (props, scope) => {
      const { resourceId, name, autoRequest, autoRequestIfEmpty } = props;

      const currentDataDst = getDataDst(props);
      const asbDataDst = currentDataDst && `/${resolveScope(currentDataDst, scope)}`;

      return {
        resourceId,
        name,
        autoRequest: autoRequest || false,
        pending: (autoRequest || autoRequestIfEmpty) || false,
        error: null,
        attempts: 0,
        dataDst: asbDataDst || [],
        requestCount: 0,
        resources: {},
        isGroup: true,
      };
    },
    useScope: true,
    createScope: false,
    cleanState: true,
  }),
  injectReducer<ResourceGroupComposedProps<any, any, any>>({
    namespace: 'Core/Resource',
    skip: (props) => !getDataDst(props),
    identifiers: ({ resourceId }) => ({ resourceId }),
    key: (props) => getDataDst(props),
    useScope: true,
    createScope: false,
    initialState: null,
    reducer: resourceGroupDataReducer,
  }),
)(ResourceGroup);

ComposedResourceGroup.type = ResourceGroup.type;

export * from './ResourceGroup';
export default ComposedResourceGroup;
